/* eslint-disable @typescript-eslint/no-unused-vars */
import { useContext, useEffect, useState } from 'react';
import { Accordion, Button, Modal, Spinner } from 'react-bootstrap';
import { BsDownload, BsFillRecordFill } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import AssessmentComponent from '../_components/assessment.component';
import ModuleChapterComponent from '../_components/moduleChapter.component';
import ScormModuleView from '../_components/scormModuleView.component';
import UserAuthStateContext from '../_contexts/userAuth.context';
import { formatModuleName } from '../_helpers/utils';
import useAPI from '../_hooks/useAPI';
import {
    LearningModuleSummary,
    ModuleChapter,
    ModuleFiles,
    ModuleTypes,
    RequestOptions,
    UserDetails
} from '../_models/types';
import { moduleService, quizService, userService } from '../_services';

const ModuleViewPage = () => {
    const [currentView, setCurrentView] = useState('');
    const { UserAuthState } = useContext(UserAuthStateContext);
    const { category } = useParams<string>();
    const [moduleNumber, setModuleNumber] = useState<number | null>(null);
    const [chapterNumber, setChapterNumber] = useState<number>(1);
    const [loadedChapter, setLoadedChapter] = useState<any>();
    const [moduleProgress, setModuleProgress] = useState<any>(null);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [moduleChapters, setModuleChapters] = useState<ModuleChapter[]>([]);
    const [moduleFiles] = useState<ModuleFiles>(() => moduleService.moduleAssets.find(x => x.name === (category as string)) as ModuleFiles);
    const [user, setUser] = useState<UserDetails | null>(null);
    const [allModules, setAllModules] = useState<any>(null);
    const [loadingCert, setLoadingCert] = useState(false);
    const [quizRes, setQuizResult] = useState<{ progress: number, result: number }>();
    const [moduleSummary, setModuleSummary] = useState<LearningModuleSummary | null>(null);
    const fetchWrapper = useAPI();
    const [showModal, setShowModal] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);

    const handleRegisterClick = () => setShowDisclaimer(true);
    const handleModalOpen = () => setShowModal(true);
    const handleModalClose = () => {
        setShowModal(false);
        setShowDisclaimer(false);
    };

    useEffect(() => {
        const getUser = async () => {
            if (UserAuthState) {
                const userDetails = await userService.getUserInformation(UserAuthState.user_id, fetchWrapper);
                setUser({ ...userDetails });
            }
        };
        getUser();
    }, [UserAuthState]);

    useEffect(() => {
        if (moduleProgress) {
            if (moduleProgress.completed) {
                setActiveIndex(moduleProgress.completed);
            }
        }
    }, [moduleProgress]);

    useEffect(() => {
        allModulesStatus();
    }, [moduleChapters, activeIndex]);

    useEffect(() => {
        quizResult();
    }, []);

    useEffect(() => {
        const getModule = async () => {
            if (UserAuthState) {
                const webModuleSummary = await moduleService.getModuleSummary(fetchWrapper);
                setModuleSummary(webModuleSummary.find(mod => mod.name === category) as LearningModuleSummary);
            }
        };
        getModule();
    }, []);

    const allModulesStatus = async () => {
        if (moduleNumber) {
            const chapterRes = await Promise.all(moduleChapters.map(async (chapt) => ({
                chapterProgress: await moduleService.getModuleCompletedStatus(fetchWrapper, moduleNumber, chapt.chapterNumber),
                chapter: chapt
            })));
            setAllModules(chapterRes);
        }
    };

    const getModuleProgress = async (chapterNo: number) => {
        if (moduleNumber && currentView !== 'assessment') {
            const prog = await moduleService.getModuleCompletedStatus(fetchWrapper, moduleNumber, chapterNo);
            setModuleProgress(prog);
            return prog;
        }
    };

    const downloadTxtFile = async (filename: string) => {
        const downloadfile = (await import(`../assets/module_guides/${filename}`)).default;
        const element = document.createElement('a');
        element.href = downloadfile;
        element.download = filename;
        element.click();
    };

    const allModulesCompleted = (): string => {
        let completedModules;
        if (allModules) {
            completedModules = allModules.map((mod: any) => mod.chapterProgress.completed === mod.chapterProgress.slides).filter(Boolean);
        }
        if (completedModules && completedModules.length !== allModules.length)
            return 'disabled';
        return '';
    };

    const getCertificate = async () => {
        setLoadingCert(true);
        try {
            const requestOptions = {
                method: 'GET',
                headers: { Authorization: `Bearer ${UserAuthState?.token}` }
            } as RequestOptions;
            const url = `${quizService.api}quiz/${quizService.quizMap(category as string)}/get_certificate/`;
            const response = await fetch(url, requestOptions);
            const _blob = await response.blob();
            const blob = new Blob([_blob]);
            const linkSource = window.URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            const fileName = `${formatModuleName(moduleSummary?.name as string)} Certificate.pdf`;
            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            downloadLink.click();
            setLoadingCert(false);
        } catch (error) {
            setLoadingCert(false);
        }
    };

    const isAssessmentPassed = () => {
        return quizRes && quizRes.progress === 100 && quizRes.result >= 80;
    };

    const setIndex = (indx: number) => {
        setActiveIndex(indx);
    };

    const quizResult = async () => {
        try {
            const res = await quizService.getQuizStatus(fetchWrapper, category as string);
            setQuizResult(res);
        } catch (err) {
            console.log(err);
        }
    };


    const downloadAiDeclarationForm = () => {
        const link = document.createElement('a');

        link.href =
            '/up_registration/Declaration-of-AI-Use-for-RtW-Advance-Courses.pdf';
        link.download =
            'Declaration-of-AI-Use-for-RtW-Advance-Courses.pdf';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const downloadCombinedPDF = (category: string) => {
        let pdfURL = '';
        let fileName = '';

        if (category === 'project_management') {
            pdfURL = '/project_management/combined_modules/Combined-Modules-Introduction-to-Project-Management.pdf';
            fileName = 'Combined-Modules-Introduction-to-Project-Management.pdf';
        } else if (category === 'introduction_to_ai_and_data_science') {
            pdfURL = '/introduction_to_ai_and_data_science/combined_modules/Combined-Modules-Introduction-to-AI-and-Data-Science.pdf';
            fileName = 'Combined-Modules-Introduction-to-AI-and-Data-Science.pdf';
        } else if (category === 'productivity') {
            pdfURL = '/productivity/combined_modules/productivity.pdf';
            fileName = 'productivity.pdf';
        } else if (category === 'money') {
            pdfURL = '/money/combined_modules/money.pdf';
            fileName = 'money.pdf';
        } else if (category === 'people') {
            pdfURL = '/people/combined_modules/people.pdf';
            fileName = 'people.pdf';
        } else if (category === 'entrepreneurial') {
            pdfURL = '/entrepreneurial/combined_modules/entrepreneurial.pdf';
            fileName = 'entrepreneurial.pdf';
        } else if (category === 'work') {
            pdfURL = '/work/combined_modules/Combined-Modules-Workskills.pdf';
            fileName = 'Combined-Modules-Workskills.pdf';
        }
        else if (category === 'presentation_skills_for_the_workplace') {
            pdfURL = '/presentation_skills_for_the_workplace/combined_modules/Presentation Skills For The Workplace.pdf';
            fileName = 'Presentation Skills For The Workplace.pdf';
        }
        else if (category === 'learn_to_unlearn_4ir_readiness') {
            pdfURL = '/learn_to_unlearn_4ir_readiness/combined_modules/Learn to Unlearn 4IR Readiness.pdf';
            fileName = 'Learn to Unlearn 4IR Readiness.pdf';
        }
        else if (category === 'introduction_to_ai') {
            pdfURL = '/introduction_to_ai/combined_modules/Introduction to AI.pdf';
            fileName = 'Introduction to AI.pdf';
        }

        else {
            console.warn('No valid category selected for download.');
            return;
        }

        const link = document.createElement('a');
        link.href = pdfURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    type Category =
        | 'blockchain'
        | 'computational_thinking'
        | 'the_gig_economy'
        | 'introduction_to_digital_literacy'
        | 'tools_for_stress_management'
        | 'embracing_change'
        | 'time_management'
        | 'creative_problem_solving'
        | 'creative_thinking'
        | 'project_management'
        | 'introduction_to_ai_and_data_science'
        | 'productivity'
        | 'money'
        | 'people'
        | 'entrepreneurial'
        | 'work'
        | 'learn_to_unlearn_4ir_readiness'
        | 'presentation_skills_for_the_workplace'
        | 'introduction_to_ai';

    const courseDetails: Record<Category, { name: string }> = {
        blockchain: { name: 'Blockchain' },
        computational_thinking: { name: 'Computational Thinking' },
        the_gig_economy: { name: 'The Gig Economy' },
        introduction_to_digital_literacy: { name: 'Introduction to Digital Literacy' },
        tools_for_stress_management: { name: 'Tools for Stress Management' },
        embracing_change: { name: 'Embracing Change' },
        time_management: { name: 'Time Management' },
        creative_problem_solving: { name: 'Creative Problem Solving' },
        creative_thinking: { name: 'Creative Thinking' },
        project_management: { name: 'Introduction to Project Management' },
        introduction_to_ai_and_data_science: { name: 'Introduction to AI and Data Science' },
        productivity: { name: 'Productivity' },
        money: { name: 'Money' },
        people: { name: 'People' },
        entrepreneurial: { name: 'Entrepreneurial' },
        work: { name: 'Work' },
        presentation_skills_for_the_workplace: { name: 'Presentation Skills for the Workplace' },
        learn_to_unlearn_4ir_readiness: { name: 'Learn to Unlearn: 4IR Readiness' },
        introduction_to_ai: { name: 'Introduction to AI' },
    };
    const currentCategory = category as Category;
    const assignmentCategories = Object.keys(courseDetails) as Category[];

    return <>
        {moduleSummary ?
            <div className="col-12 module-view">
                <div className="row">
                    <div className="hero-image">
                        {
                            moduleSummary.is_scorm ? <img src="/images/J_FAQ.jpg" alt="" className="w-100 img-fluid" /> :
                                <img src="/images/M_WELCOME_back.jpg" alt="" className="w-100 img-fluid" />
                        }
                        <div className="h-100 w-100 hero-image__title">
                            <div className="container text-center">
                                <h3>
                                    {category && `${formatModuleName(category)} Module`}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-12 module-view__body">
                        <div className="row">
                            {
                                <div className="col-12 col-lg-3">
                                    <div className="col-12 module-view__module_info sticky-lg-top">
                                        <div className="row">
                                            <Accordion defaultActiveKey="0">
                                                <Accordion.Item eventKey="0">
                                                    <Accordion.Header>
                                                        <div className="col-10 module-view__module_info__header-text">
                                                            <p>Topics</p>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body>
                                                        <div className="col-12 module-view__module_info__body">
                                                            <ul className="row module-view__module_info__body-list-container">
                                                                {
                                                                    moduleSummary?.is_scorm ?
                                                                        <li key="0"
                                                                            className="col-12 module-view__module_info__body-list-item"
                                                                            onClick={() => {
                                                                                setCurrentView('Content');
                                                                                setChapterNumber(1);
                                                                            }
                                                                            }>
                                                                            <div className="row">
                                                                                <div
                                                                                    className="col-2 module-view--selected">
                                                                                    {BsFillRecordFill({}) as JSX.Element}
                                                                                </div>
                                                                                <div
                                                                                    className="col-10 module-view--selected">
                                                                                    <p>{'Content'}</p>
                                                                                </div>
                                                                            </div>
                                                                        </li> : null
                                                                }

                                                                {
                                                                    (category === 'introduction_to_ai' || category === 'learn_to_unlearn_4ir_readiness' || category === 'presentation_skills_for_the_workplace' || category === 'project_management' || category === 'introduction_to_ai_and_data_science' || category === 'productivity' || category === 'work') &&
                                                                    <div>
                                                                        <div className="row">
                                                                            <li className="col-12 module-view__module_info__body-list-item">
                                                                                <Button
                                                                                    variant="primary"
                                                                                    onClick={() => downloadCombinedPDF(category)}
                                                                                    className="w-100"
                                                                                >
                                                                                    Download Content
                                                                                </Button>
                                                                            </li>
                                                                        </div>
                                                                    </div>
                                                                }

                                                                {
                                                                    user && !user.is_admin &&
                                                                    <li className={`col-12 module-view__module_info__body-list-item ${moduleSummary?.is_scorm ? '' : allModulesCompleted()
                                                                        }`}>
                                                                        <div className="row" onClick={() => {
                                                                            setCurrentView('assessment');
                                                                            setChapterNumber(0);
                                                                        }}>
                                                                            <div
                                                                                className={`col-2 ${currentView === 'assessment' ? 'module-view--selected' : ''}`}>
                                                                                {BsFillRecordFill({}) as JSX.Element}
                                                                            </div>
                                                                            <div
                                                                                className={`col-10 ${currentView === 'assessment' ? 'module-view--selected' : ''}`}>
                                                                                <p>Assessment</p>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                }

                                                            </ul>
                                                        </div>
                                                    </Accordion.Body>
                                                </Accordion.Item>

                                                {
                                                    !moduleSummary?.is_scorm &&
                                                    <Accordion.Item eventKey="1">
                                                        <Accordion.Header>
                                                            <div
                                                                className="col-10 module-view__module_info__header-text">
                                                                <p>Resources</p>
                                                            </div>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <div className="col-12 myLearning__module__downloads">
                                                                {
                                                                    user && user.is_admin &&
                                                                    <div className="col-12 download-item"
                                                                        onClick={() => downloadTxtFile(moduleFiles.moduleGuide)}>
                                                                        <div className="row">
                                                                            <div className="col-3 download-item__icon">
                                                                                {BsDownload({}) as JSX.Element}
                                                                            </div>
                                                                            <div className="col-9 download-item__text">
                                                                                <p>Facilitator Guide</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                }
                                                                {
                                                                    moduleFiles.files.map((file, indx) =>
                                                                        <div key={indx}
                                                                            className="col-12 download-item"
                                                                            onClick={() => downloadTxtFile(file)}>
                                                                            <div className="row">
                                                                                <div
                                                                                    className="col-3 download-item__icon">
                                                                                    {BsDownload({}) as JSX.Element}
                                                                                </div>
                                                                                <div
                                                                                    className="col-9 download-item__text">
                                                                                    <p>Topic {indx + 1}</p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                {
                                                                    user && user.is_admin &&
                                                                    <div className="col-12 download-item"
                                                                        onClick={() => downloadTxtFile(moduleFiles.assessment)}>
                                                                        <div className="row">
                                                                            <div className="col-3 download-item__icon">
                                                                                {BsDownload({}) as JSX.Element}
                                                                            </div>
                                                                            <div className="col-9 download-item__text">
                                                                                <p>Assessment</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                }
                                                                {
                                                                    user && user.is_admin &&
                                                                    <div className="col-12 download-item"
                                                                        onClick={() => downloadTxtFile(moduleFiles.answer)}>
                                                                        <div className="row">
                                                                            <div className="col-3 download-item__icon">
                                                                                {BsDownload({}) as JSX.Element}
                                                                            </div>
                                                                            <div className="col-9 download-item__text">
                                                                                <p>Answers</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                }
                                                            </div>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                }

                                                {
                                                    user && !user.is_admin &&
                                                    <Accordion.Item eventKey="2">
                                                        <Accordion.Header>
                                                            <div
                                                                className="col-10 module-view__module_info__header-text">
                                                                <p>Certification</p>
                                                            </div>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <div className="col-12 myLearning__module__downloads">
                                                                {

                                                                    <div
                                                                        className={`col-12 download-item ${isAssessmentPassed() && !loadingCert ? 'download-item--active' : 'download-item--inactive'}`}>
                                                                        <div className="row" onClick={getCertificate}>
                                                                            <div className="col-3 download-item__icon">
                                                                                {
                                                                                    loadingCert ?
                                                                                        <Spinner animation="border"
                                                                                            size="sm" /> :
                                                                                        BsDownload({}) as JSX.Element}
                                                                            </div>

                                                                            <div className="col-9 download-item__text">
                                                                                <p>Download Certificate</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                }
                                                            </div>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                }

                                                {
                                                    (currentCategory === 'project_management' || currentCategory === 'introduction_to_ai_and_data_science') && (
                                                        <div className="row">
                                                            <div
                                                                style={{ cursor: !isAssessmentPassed() ? 'not-allowed' : 'pointer' }}
                                                                className="col-12 module-view__module_info__body-list-item">
                                                                <Button
                                                                    variant="primary"
                                                                    disabled={!isAssessmentPassed()}
                                                                    onClick={handleModalOpen}
                                                                    className="w-100"
                                                                >
                                                                    UP Registration
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )
                                                }

                                            </Accordion>
                                        </div>
                                    </div>
                                </div>
                            }
                            <div className={'col-12 col-lg-9'}>
                                <div className="row">
                                    {
                                        currentView === 'assessment' ?
                                            <AssessmentComponent
                                                moduleNumber={moduleNumber as number}
                                                moduleName={category as string}
                                                quizResult={quizResult} /> :
                                            moduleSummary.is_scorm ?
                                                <ScormModuleView moduleSummary={moduleSummary} /> :
                                                loadedChapter && <ModuleChapterComponent
                                                    category={category as string}
                                                    fetchWrapper={fetchWrapper}
                                                    loadedChapter={loadedChapter}
                                                    chapterNumber={chapterNumber}
                                                    moduleNumber={moduleNumber as number}
                                                    getModuleProgress={getModuleProgress}
                                                    moduleProgress={moduleProgress}
                                                    activeIndex={activeIndex}
                                                    allModules={allModules}
                                                    setIndex={setIndex}
                                                    allModulesStatus={allModulesStatus}
                                                />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div> : 'Loading....'
        }

        {
            (currentCategory === 'project_management' || currentCategory === 'introduction_to_ai_and_data_science') && (
                <Modal show={showModal} onHide={handleModalClose} centered size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>UP Registration</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>You need to do an assignment:</p>
                        <div
                            className="disclaimer-box mt-3 p-3 border rounded"
                            style={{
                                fontSize: '12px',
                                backgroundColor: '#f8f9fa',
                                textAlign: 'justify',
                                lineHeight: '1.6'
                            }}
                        >
                            {showDisclaimer ? (
                                <p>
                                    By clicking on Register and Submit Assignment button, you will leave the Absa RtW
                                    app.
                                    Any personal information provided by you to Enterprises University of Pretoria will
                                    be subjected to their Personal Information and Data storage and processing
                                    requirements.
                                    You will be required to give consent to Enterprises UP to process your personal data
                                    for the purposes of obtaining Certification for{' '}
                                    {({
                                        blockchain: 'Blockchain',
                                        computational_thinking: 'Computational Thinking',
                                        the_gig_economy: 'The Gig Economy',
                                        introduction_to_digital_literacy: 'Introduction to Digital Literacy',
                                        tools_for_stress_management: 'Tools for Stress Management',
                                        embracing_change: 'Embracing Change',
                                        time_management: 'Time Management',
                                        creative_problem_solving: 'Creative Problem Solving',
                                        creative_thinking: 'Creative Thinking',
                                        project_management: 'Introduction to Project Management',
                                        introduction_to_ai_and_data_science: 'Introduction to AI and Data Science',
                                        productivity: 'Productivity',
                                        money: 'Money',
                                        people: 'People',
                                        entrepreneurial: 'Entrepreneurial',
                                        work: 'Work',
                                    }[currentCategory] || '')}.
                                </p>
                            ) : (
                                <>
                                    {currentCategory ? (
                                        <p>
                                            As an Absa ReadytoWork user, you can apply for an Enterprises University of
                                            Pretoria (Enterprises UP) certification of the {courseDetails[currentCategory].name}
                                            {' '}Course. To qualify for this certification, you would be required to
                                            complete an assignment that will be marked by the lecturer of the said
                                            course. If you pass this assignment, you will be issued a Certificate of
                                            Completion by Enterprises UP.
                                            <br />
                                            To start your Enterprises UP certification process, you will need to
                                            Register by providing personal information required by Enterprises UP via
                                            the Google Form link provided. You will also need to upload your RtW{' '}
                                            {courseDetails[currentCategory].name} Certificate, your completed
                                            assignment, and your completed Declaration of AI Use form. Please
                                            download and complete the declaration before submitting your assignment.
                                        </p>
                                    ) : null

                                    }
                                </>
                            )}
                        </div>

                        <div className="d-flex flex-column flex-lg-row w-100 gap-3" style={{ marginTop: '12px' }}>
                            <Button
                                className="flex-fill text-center"

                                onClick={() => {

                                    let assignmentLink = '';
                                    let fileName = '';

                                    if (category === 'introduction_to_ai_and_data_science') {
                                        assignmentLink = '/introduction_to_ai_and_data_science/assignment/Assignment-Introduction-to-AI-and-Data-Science.pdf';
                                        fileName = 'Assignment-Introduction-to-AI-and-Data-Science.pdf';
                                    } else if (category === 'productivity') {
                                        assignmentLink = '/productivity/assignment/Eisenhower_Matrix.pdf';
                                        fileName = 'Eisenhower_Matrix.pdf';
                                    } else if (category === 'project_management') {
                                        assignmentLink = '/project_management/assignment/Assignment-Introduction-to-Project-Management.docx';
                                        fileName = 'Assignment-Introduction-to-Project-Management.docx';
                                    } else {
                                        console.warn('No valid category for assignment');
                                        return;
                                    }

                                    const link = document.createElement('a');
                                    link.href = assignmentLink;
                                    link.download = fileName;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                            >
                                Download Assignment
                            </Button>

                            <Button
                                className="flex-fill text-center"

                                onClick={downloadAiDeclarationForm}
                            >
                                Download AI Declaration Form
                            </Button>

                            <Button
                                className="flex-fill text-center"

                                onClick={handleRegisterClick}
                            >
                                Register and Submit Assignment
                            </Button>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        {showDisclaimer && (
                            <Button
                                variant="primary"
                                onClick={() => {
                                    const formLink =
                                        category === 'introduction_to_ai_and_data_science'
                                            ? 'https://forms.gle/bH3okrvirGnzMozJ8'
                                            : 'https://forms.gle/ffsQHs8YdNVbz4vY8';
                                    window.open(formLink, '_blank', 'noopener,noreferrer');
                                }}
                            >
                                Continue
                            </Button>
                        )}
                        <Button variant="secondary" onClick={handleModalClose}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )
        }
    </>;
};

export default ModuleViewPage;
